// ============================================
// @gs/auth — Azure AD Authentication pour GS
// ============================================

import { createContext, useContext } from 'react';

// ---- Types ----

/** Rôles applicatifs GS (mappés depuis Azure AD groups) */
export enum GsRole {
  ADMIN = 'ADMIN',
  GESTIONNAIRE = 'GESTIONNAIRE',
  UTILISATEUR = 'UTILISATEUR',
}

/** Utilisateur GS authentifié via Azure AD */
export interface GsUser {
  /** Object ID Azure AD */
  id: string;
  /** Nom complet (displayName) */
  name: string;
  /** Email M365 */
  email: string;
  /** Rôle applicatif */
  role: GsRole;
  /** Régions/points de service autorisés (vide = tous pour ADMIN) */
  allowedServicePoints: string[];
  /** Initiales pour avatar */
  initials: string;
  /** Photo de profil Azure AD (URL blob ou base64) */
  photoUrl?: string;
}

/** État du contexte d'authentification */
export interface AuthState {
  /** Utilisateur courant ou null si pas connecté */
  user: GsUser | null;
  /** Chargement en cours (vérification token) */
  isLoading: boolean;
  /** Erreur d'auth */
  error: string | null;
  /** Boucle redirect MSAL détectée (seuil 3/60 s) — voir AUTH_ROBUSTNESS.md */
  loopDetected: boolean;
  /** Login via Azure AD redirect */
  login: () => Promise<void>;
  /** Logout + redirect */
  logout: () => Promise<void>;
  /** Purge cache MSAL + loginRedirect(prompt:login) — self-heal boucle */
  resetAndReauth: () => Promise<void>;
  /** Token d'accès pour les appels API — `forceRefresh` force un refresh MSAL (bypass cache) */
  getAccessToken: (options?: { forceRefresh?: boolean }) => Promise<string>;
}

// ---- Context ----

export const AuthContext = createContext<AuthState>({
  user: null,
  isLoading: true,
  error: null,
  loopDetected: false,
  login: async () => {},
  logout: async () => {},
  resetAndReauth: async () => {},
  getAccessToken: async (_options?: { forceRefresh?: boolean }) => '',
});

/** Hook pour accéder à l'auth dans les composants */
export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth doit être utilisé dans un AuthProvider');
  }
  return context;
}

// ---- Guards ----

/** Vérifie si l'utilisateur a le rôle requis */
export function hasRole(user: GsUser | null, requiredRole: GsRole): boolean {
  if (!user) return false;
  const hierarchy: Record<GsRole, number> = {
    [GsRole.ADMIN]: 3,
    [GsRole.GESTIONNAIRE]: 2,
    [GsRole.UTILISATEUR]: 1,
  };
  return hierarchy[user.role] >= hierarchy[requiredRole];
}

/** Vérifie si l'utilisateur a accès à un point de service */
export function hasServicePointAccess(user: GsUser | null, servicePointId: string): boolean {
  if (!user) return false;
  if (user.role === GsRole.ADMIN) return true;
  return user.allowedServicePoints.includes(servicePointId);
}

// ---- Composants ----
// NOTE: AuthProvider et ProtectedRoute sont dans des fichiers séparés
// pour éviter les imports circulaires (ils importent depuis ce fichier)
export { AuthProvider } from './AuthProvider';
export type { AuthProviderProps, RoleMapping } from './AuthProvider';
export { ProtectedRoute } from './ProtectedRoute';
export type { ProtectedRouteProps } from './ProtectedRoute';

// ---- Helpers JWT exportés (consommables par apps pour tests + guards) ----
export { isJwtExpired, AuthTokenExpiredError, selectJwtForOwnBackend, acquireJwtForOwnBackend } from './acquireJwtForOwnBackend';
